import { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useLocation, useSearchParams } from "react-router-dom";
import { useApiClient } from "../../api/client";
import { useAuth } from "../../api/auth.context";

import { MatchType } from "../../data/enums/generic";
import { Container, Grid } from "@mui/material";
import { selectCustomer } from "../../utils/customer";
import { OperationStatus } from "../../data/enums/operations";
import { keywordsBidOptimizationColumns } from "../../data/tables/operations";

import ExecuteButton from "./ExecuteButton";
import CardLabel from "../../components/CardLabel";
import OperationPreviewHeader from "./OperationPreviewHeader";
import OperationExecutionStatus from "./OperationExecutionStatus";

import operationTransformers from "../../utils/transformers/operations";


export default function KeyworsdBidOptimizationOperationPreview() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const apiClient = useApiClient();
  const { currentUser, updateUser } = useAuth();

  const operationId = location.pathname.split("/").pop();
  const [operation, setOperation] = useState({
    generated: null,
    isSkag: false,
    id: null
  });

  const [operationStatus, setOperationStatus] = useState({
    status: OperationStatus.PENDING,
    error: ""
  });

  const [exactMatchKeywords, setExactMatchKeywords] = useState([]);
  const [broadMatchKeywords, setBroadMatchKeywords] = useState([]);

  const [selectedExactMatchKeywords, setSelectedExactMatchKeywords] = useState([]);
  const [selectedBroadMatchKeywords, setSelectedBroadMatchKeywords] = useState([]);

  useEffect(() => {
    // Make sure to select the correct customer for the request
    selectCustomer(currentUser, searchParams, updateUser);
  }, [])

  useEffect(() => {
    apiClient.operation(currentUser, operationId).then((operation) => {
      const items = operation.items.map(operationTransformers.keywordsBidOptimization);

      const exactMatchKeywords = items.filter((item) => item.matchType === MatchType.EXACT);
      const broadMatchKeywords = items.filter((item) => {
        return (item.matchType === MatchType.BROAD) || (item.matchType === MatchType.PHRASE)
      });

      setExactMatchKeywords(exactMatchKeywords);
      setBroadMatchKeywords(broadMatchKeywords);

      setOperation({
        generated: operation.generated,
        isSkag: operation.isSkag,
        id: operation.id
      });
    })
  }, [])

  const execute = function () {
    setOperationStatus({
      ...operationStatus,
      status: OperationStatus.IN_EXECUTION
    });

    const payload = {
      // Merge exact and broad keywords
      targets: selectedExactMatchKeywords.concat(selectedBroadMatchKeywords).map((id) => {
        const ids = id.split("/");
  
        // FIXME: Where is the bid?
        return {
          "keywordId": parseInt(ids[1]),
          "adGroupId": parseInt(ids[0])
        }
      })
    };

    apiClient.executeOperation(currentUser, operationId, payload).then(() => {
      setOperationStatus({
        ...operationStatus,
        status: OperationStatus.EXECUTED
      });
    }).catch((error) => {
      if (error.response) {
        setOperationStatus({
          status: OperationStatus.FAILED,
          error: error.response.data.message
        });
      }
    });
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        marginTop: 4
      }}
    >
      <Grid container>
        <Grid item xs={12}>
          <OperationPreviewHeader
            operation={operation}
            title={"Keywords Bid Optimization"}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            marginTop: 4
          }}
        >
          <CardLabel text={"Broad Match Keywords"} sx={{ marginBottom: 1 }} />
          <div style={{ height: 400, width: "100%" }}>
            <DataGrid
              experimentalFeatures={{ newEditingApi: true }}
              rows={broadMatchKeywords}
              columns={keywordsBidOptimizationColumns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              checkboxSelection
              disableSelectionOnClick
              onSelectionModelChange={setSelectedBroadMatchKeywords}
            />
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            marginTop: 4
          }}
        >
          <CardLabel text={"Exact Match Keywords"} sx={{ marginBottom: 1 }} />
          <div style={{ height: 400, width: "100%" }}>
            <DataGrid
              experimentalFeatures={{ newEditingApi: true }}
              rows={exactMatchKeywords}
              columns={keywordsBidOptimizationColumns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              checkboxSelection
              disableSelectionOnClick
              onSelectionModelChange={setSelectedExactMatchKeywords}
            />
          </div>
        </Grid>

        <Grid
          item
          xs={12}
        >
          <OperationExecutionStatus
            status={operationStatus.status}
            error={operationStatus.error}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sx={{
            marginTop: 2,
            marginBottom: 2,
            textAlign: "right"
          }}
        >
          <ExecuteButton execute={execute} operationStatus={operationStatus.status} />
        </Grid>
      </Grid>
    </Container>
  );
}